import { Name } from "@ndn/packet";
import { assert, concatBuffers, Reorder } from "@ndn/util";
import { collect, map, parallelMap, writeToStream } from "streaming-iterables";
import { UnverifiedFetcher } from "./unverified_browser.js";
class FetchResult {
    name;
    opts;
    constructor(name, opts) {
        this.name = name;
        this.opts = opts;
    }
    get count() { return this.uvf?.count ?? 0; }
    uvf;
    promise;
    startFetcher() {
        assert(!this.uvf, "fetch.Result is already used");
        const opts = {
            ...this.opts.cOpts,
            ...this.opts,
        };
        this.uvf = new UnverifiedFetcher(this.name, opts);
        return parallelMap(16, async ({ seg, data }) => {
            await opts.verifier?.verify(data);
            return { seg, data };
        }, this.uvf.fetch());
    }
    unordered() {
        return map(({ data, seg: segNum }) => Object.assign(data, { segNum }), this.startFetcher());
    }
    async *ordered() {
        const reorder = new Reorder(this.opts.segmentRange?.[0]);
        for await (const { seg: segNum, data } of this.startFetcher()) {
            reorder.push(segNum, data);
            yield* reorder.shift();
        }
        assert(reorder.empty, `${reorder.size} leftover segments`);
    }
    chunks() {
        return map((data) => data.content, this.ordered());
    }
    pipe(dest) {
        return writeToStream(dest, this.chunks());
    }
    async startPromise() {
        const chunks = await collect(this.chunks());
        return concatBuffers(chunks);
    }
    // eslint-disable-next-line unicorn/no-thenable
    then(onfulfilled, onrejected) {
        this.promise ??= this.startPromise();
        // eslint-disable-next-line promise/prefer-await-to-then
        return this.promise.then(onfulfilled, onrejected);
    }
    [Symbol.asyncIterator]() {
        return this.ordered()[Symbol.asyncIterator]();
    }
}
/**
 * Fetch a segmented object.
 *
 * @remarks
 * This function does not perform version discovery. If the segmented object is versioned, `name`
 * must include the version component. You can perform version discovery with
 * {@link discoverVersion} function and pass its result to this function for fetching the
 * versioned and segmented object.
 */
export function fetch(name, opts = {}) {
    return new FetchResult(Name.from(name), opts);
}
